import React, { useEffect, useMemo, useState } from 'react';
import AccountSwitcher from '../../../components/AccountSwitcher';
import { useAccounts } from '../../../context/AccountContext';

export default function AudiencesTest() {
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('GOOGLE_ADS');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [rows, setRows] = useState([]);
  const [raw, setRaw] = useState(null);
  const [custom, setCustom] = useState([]);
  const [openCA, setOpenCA] = useState({});
  const [customerId, setCustomerId] = useState('');
  const [filter, setFilter] = useState('');

  const brandId = useMemo(() => {
    try {
      const current = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId');
      if (current) return current;
    } catch (_) {}
    const tryKeys = ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id'];
    for (const k of tryKeys) {
      try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {}
    }
    const tryJson = ['selectedBrand', 'currentBrand', 'activeBrand'];
    for (const k of tryJson) {
      try {
        const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (raw) { const obj = JSON.parse(raw); if (obj?.id) return obj.id; }
      } catch(_) {}
    }
    return '';
  }, []);

  useEffect(() => {
    // Try to auto-fill customerId from connections
    const boot = async () => {
      try {
        setLoading(true);
        let cid = '';
        if (brandId) {
          const resp = await fetch('https://api.glowytics.com/api/auth/connections', {
            headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          if (resp.ok) {
            const data = await resp.json();
            let found = null;
            if (Array.isArray(data)) {
              found = data.find(x => (x?.platform === 'GOOGLE_ADS') || (x?.platform === 'google_ads')) || null;
            } else if (data && typeof data === 'object') {
              const ga = data['GOOGLE_ADS'] || data['google_ads'] || null;
              if (ga) found = ga;
            }
            cid = found?.customerId || '';
          }
        }
        if (cid) setCustomerId(String(cid));
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [brandId]);

  const fetchAudiences = async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      setRows([]);
      setRaw(null);
      const cid = (customerId || '').trim();
      if (!cid) throw new Error('customerId required');
      const url = `https://api.glowytics.com/api/google-ads/audiences/list?customerId=${encodeURIComponent(cid)}`;
      const resp = await fetch(url, {
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      const txt = await resp.text(); let js; try { js = JSON.parse(txt); } catch { js = { raw: txt }; }
      if (!resp.ok) throw new Error(js?.error || resp.statusText || 'Request failed');
      setRaw(js);
      const arr = Array.isArray(js?.audiences) ? js.audiences : (Array.isArray(js?.data) ? js.data : []);
      const norm = (arr || []).map(a => ({
        id: a.id || a.userListId || a.resourceName || '',
        name: a.name || a.displayName || '',
        type: a.type || a.listType || a.userListType || '',
        status: a.status || a.listStatus || a.userListStatus || '',
        size: a.sizeForSearch || a.sizeForDisplay || a.size || null,
        sizeForSearch: a.sizeForSearch || null,
        sizeForDisplay: a.sizeForDisplay || null,
        description: a.description || '',
        source: a.origin || a.membershipStatus || '',
        resourceName: a.resourceName || '',
      }));
      setRows(norm);
      const ca = Array.isArray(js?.customAudiences) ? js.customAudiences : [];
      setCustom(ca);
    } catch (e) {
      setErrorMsg(e?.message || 'Load error');
    } finally {
      setLoading(false);
    }
  };

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const base = q ? rows.filter(r =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.type || '').toLowerCase().includes(q) ||
      (r.status || '').toLowerCase().includes(q)
    ) : rows;
    // default: sort by size desc (prefer search then display)
    const score = (r) => {
      const s = (r.size != null ? Number(r.size) : null);
      const ss = (r.sizeForSearch != null ? Number(r.sizeForSearch) : null);
      const sd = (r.sizeForDisplay != null ? Number(r.sizeForDisplay) : null);
      return (s != null ? s : (ss != null ? ss : (sd != null ? sd : -1)));
    };
    const copy = [...base];
    copy.sort((a,b) => (score(b) - score(a)));
    return copy;
  }, [rows, filter]);

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Google Ads Audiences - Test</h1>
          <p className="text-sm text-gray-500">This page lists audiences from the connected Google Ads account.</p>
        </div>
        <div className="bg-white rounded-2xl border shadow-sm p-5 mb-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Customer ID</label>
              <input
                value={customerId}
                onChange={(e)=>setCustomerId(e.target.value)}
                placeholder="e.g. 123-456-7890"
                className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              />
            </div>
            <button
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60"
              onClick={fetchAudiences}
              disabled={loading || !customerId}
            >
              {loading ? 'Loading…' : 'Fetch Audiences'}
            </button>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Search</label>
              <input
                value={filter}
                onChange={(e)=>setFilter(e.target.value)}
                placeholder="Filter by name/type/status…"
                className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              />
            </div>
          </div>
          {errorMsg ? <div className="text-sm text-red-600 mt-3">{errorMsg}</div> : null}
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-gray-800">Audiences</div>
            <div className="text-xs text-gray-500">{visible.length} item(s)</div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left">name</th>
                  <th className="px-3 py-2 text-left">type</th>
                  <th className="px-3 py-2 text-left">status</th>
                  <th className="px-3 py-2 text-right" title="sorted by size desc">size</th>
                  <th className="px-3 py-2 text-left">source</th>
                  <th className="px-3 py-2 text-left">resource</th>
                </tr>
              </thead>
              <tbody>
                {visible.length ? visible.map((r, i) => (
                  <tr key={r.id || r.resourceName || i} className="border-t">
                    <td className="px-3 py-2 max-w-[320px] truncate" title={r.name}>{r.name || '-'}</td>
                    <td className="px-3 py-2">{r.type || '-'}</td>
                    <td className="px-3 py-2">{r.status || '-'}</td>
                    <td className="px-3 py-2 text-right">{r.size != null ? Number(r.size).toLocaleString('tr-TR') : '-'}</td>
                    <td className="px-3 py-2">{r.source || '-'}</td>
                    <td className="px-3 py-2 max-w-[360px] truncate" title={r.resourceName}>{r.resourceName || '-'}</td>
                  </tr>
                )) : (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={6}>{loading ? 'Loading…' : 'No data'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border shadow-sm p-5">
          <div className="text-sm font-medium text-gray-800 mb-2">Raw response</div>
          <pre className="text-[11px] bg-gray-50 p-3 rounded-lg overflow-x-auto max-h-80">{raw ? JSON.stringify(raw, null, 2) : '—'}</pre>
        </div>

        {/* Custom Audiences */}
        <div className="bg-white rounded-2xl border shadow-sm p-5 mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-gray-800">Custom Audiences</div>
            <div className="text-xs text-gray-500">{(custom || []).length} item(s)</div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left">name</th>
                  <th className="px-3 py-2 text-left">type</th>
                  <th className="px-3 py-2 text-left">status</th>
                  <th className="px-3 py-2 text-left">description</th>
                  <th className="px-3 py-2 text-left">members</th>
                </tr>
              </thead>
              <tbody>
                {(custom || []).length ? custom.map((c, i) => {
                  const key = c.id || c.resourceName || String(i);
                  const isOpen = !!openCA[key];
                  const preview = Array.isArray(c.members) ? c.members.slice(0, 3) : [];
                  return (
                    <React.Fragment key={key}>
                      <tr className="border-t align-top">
                        <td className="px-3 py-2">
                          <button
                            onClick={()=>setOpenCA(prev => ({ ...prev, [key]: !prev[key] }))}
                            className="text-left font-medium text-gray-800 hover:underline"
                            title={c.name}
                          >
                            {isOpen ? '▾ ' : '▸ '} {c.name || '-'}
                          </button>
                        </td>
                        <td className="px-3 py-2">{c.type || '-'}</td>
                        <td className="px-3 py-2">{c.status || '-'}</td>
                        <td className="px-3 py-2 max-w-[360px] truncate" title={c.description}>{c.description || '-'}</td>
                        <td className="px-3 py-2 max-w-[420px] truncate" title={Array.isArray(c.members) ? JSON.stringify(c.members) : ''}>
                          {preview.length ? preview.map((m, idx) => {
                            let memberType = '';
                            let paramText = '';
                            if (typeof m === 'string') {
                              paramText = m;
                            } else if (m && typeof m === 'object') {
                              memberType = m.memberType || '';
                              if (typeof m.parameter === 'string') paramText = m.parameter;
                              else if (m.parameter != null) {
                                try { paramText = JSON.stringify(m.parameter); } catch { paramText = String(m.parameter); }
                              }
                            }
                            const label = [memberType, paramText].filter(Boolean).join(': ');
                            return <span key={idx} className="inline-block mr-1 px-1.5 py-0.5 bg-gray-100 rounded">{(label || '').slice(0, 100)}</span>;
                          }) : '—'}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t bg-gray-50">
                          <td className="px-3 py-3" colSpan={5}>
                            {Array.isArray(c.members) && c.members.length ? (
                              <div className="space-y-2">
                                <div className="text-xs text-gray-500">Members ({c.members.length})</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {c.members.map((m, idx) => {
                                    const mt = typeof m === 'string' ? '' : (m?.memberType || '');
                                    let param = '';
                                    if (typeof m === 'string') param = m;
                                    else if (m && typeof m === 'object') {
                                      if (typeof m.parameter === 'string') param = m.parameter;
                                      else if (m.parameter != null) {
                                        try { param = JSON.stringify(m.parameter, null, 0); } catch { param = String(m.parameter); }
                                      }
                                    }
                                    return (
                                      <div key={idx} className="p-2 bg-white border rounded">
                                        <div className="text-[11px] text-gray-400">{mt || 'member'}</div>
                                        <div className="text-xs text-gray-800 break-all">{param || '—'}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500">No member details available for this custom audience.</div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }) : (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}


